import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { apartments, entrances, floors } from "./apartmentsData";
import "./FloorMap.css";

function Floor({ entranceParam, floorParam }) {
  const { t } = useTranslation("portfolio");
  const [floor, setFloor] = useState(null);
  const [entrance, setEntrance] = useState("");
  const [hoveredApartment, setHoveredApartment] = useState(null);
  const imageRef = useRef(null);
  const [imageDimensions, setImageDimensions] = useState({
    width: 0,
    height: 0,
  });
  const [imageScale, setImageScale] = useState({ x: 1, y: 1 });
  const [initialized, setInitialized] = useState(false);

  const activeApt = apartments.find((a) => a.id === hoveredApartment);
  const floorImage = `/innerpages/assets/img/floor/floor_${entrance}_${floor}.png`;

  const getTranslatedStatus = (status) => {
    switch (status) {
      case "Свободен":
        return t("apartmentDetails.status.available");
      case "Резервиран":
        return t("apartmentDetails.status.reserved");
      case "Продаден":
        return t("apartmentDetails.status.sold");
      default:
        return status;
    }
  };

  const handleImageLoad = () => {
    if (imageRef.current) {
      const displayedWidth = imageRef.current.offsetWidth;
      const displayedHeight = imageRef.current.offsetHeight;
      const naturalWidth = imageRef.current.naturalWidth;
      const naturalHeight = imageRef.current.naturalHeight;

      setImageDimensions({ width: displayedWidth, height: displayedHeight });
      setImageScale({
        x: displayedWidth / naturalWidth,
        y: displayedHeight / naturalHeight,
      });
    }
  };

  const scaleCoordinates = (coords) => {
    if (!coords) return "";
    const coordArray = coords.split(",").map(Number);
    const scaledCoords = [];

    for (let i = 0; i < coordArray.length; i += 2) {
      const x = coordArray[i] * imageScale.x;
      const y = coordArray[i + 1] * imageScale.y;
      scaledCoords.push(Math.round(x), Math.round(y));
    }

    return scaledCoords.join(",");
  };

  const getPolygonCentroid = (coords) => {
    if (!coords) return { x: 0, y: 0 };
    const coordArray = coords.split(",").map(Number);
    const points = [];

    for (let i = 0; i < coordArray.length; i += 2) {
      points.push({
        x: coordArray[i] * imageScale.x,
        y: coordArray[i + 1] * imageScale.y,
      });
    }

    if (points.length === 0) return { x: 0, y: 0 };

    const centroid = points.reduce(
      (acc, point) => ({
        x: acc.x + point.x,
        y: acc.y + point.y,
      }),
      { x: 0, y: 0 }
    );

    return { x: centroid.x / points.length, y: centroid.y / points.length };
  };

  const handleAreaClick = (e, aptId, aptFloor, aptEntrance) => {
    e.preventDefault();
    const firstChar = aptId?.trim()?.charAt(0);
    const firstCharCode = firstChar?.charCodeAt(0);
    const isParking = firstCharCode === 1055 || firstCharCode === 1087;

    if (isParking) return;

    window.location.href = `/innerpages/apartament-details?id=${aptId}&floor=${aptFloor}&entrance=${aptEntrance}`;
  };

  const handleAreaHover = (aptId) => setHoveredApartment(aptId);
  const handleAreaLeave = () => setHoveredApartment(null);

  useEffect(() => {
    if (initialized) return;

    let newEntrance = "";
    let newFloor = null;

    if (entranceParam) {
      newEntrance = entranceParam;
    } else {
      const savedEntrance = sessionStorage.getItem("selectedEntrance");
      if (savedEntrance) newEntrance = savedEntrance;
      else if (entrances.length) newEntrance = entrances[0];
    }

    if (floorParam !== null && floorParam !== undefined) {
      newFloor = floorParam;
    } else {
      const savedFloor = sessionStorage.getItem("selectedFloor");
      if (savedFloor) newFloor = Number(savedFloor);
      else if (floors.length) newFloor = floors[0];
    }

    if (newEntrance) setEntrance(newEntrance);
    if (newFloor !== null) setFloor(newFloor);
    setInitialized(true);
  }, [entranceParam, floorParam, floors, entrances, initialized]);

  const handleFloorChange = (newFloor) => {
    setFloor(newFloor);
    sessionStorage.setItem("selectedFloor", newFloor);
    const url = new URL(window.location);
    url.searchParams.set("floor", newFloor);
    if (entrance) url.searchParams.set("entrance", entrance);
    window.history.replaceState({}, "", url);
  };

  const handleEntranceChange = (newEntrance) => {
    setEntrance(newEntrance);
    sessionStorage.setItem("selectedEntrance", newEntrance);
    const url = new URL(window.location);
    url.searchParams.set("entrance", newEntrance);
    if (floor) url.searchParams.set("floor", floor);
    window.history.replaceState({}, "", url);
  };

  const filteredApartments = apartments.filter(
    (apt) => apt.entrance === entrance && apt.floor === floor
  );

  return (
    <>
      <div className="floor-layout">
        <div className="floor-image">
          <img
            ref={imageRef}
            src={floorImage}
            alt={t("floorMap.floorPlanAlt")}
            useMap="#image-map"
            onLoad={handleImageLoad}
          />

          <svg
            className="area-highlight-overlay"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: imageDimensions.width,
              height: imageDimensions.height,
              pointerEvents: "none",
              zIndex: 1,
            }}
          >
            {filteredApartments.map((apt) => {
              const centroid = getPolygonCentroid(apt.coords);
              const isNotFree = ["резервиран", "продаден"].includes(
                apt.status.toLowerCase()
              );
              const isHovered = hoveredApartment === apt.id;

              const firstChar = apt.id?.trim()?.charAt(0);
              const firstCharCode = firstChar?.charCodeAt(0);
              const isP = firstCharCode === 1055 || firstCharCode === 1087;

              let fill, stroke, opacity;
              if (isNotFree) {
                fill = isHovered
                  ? "rgba(220, 53, 69, 0.35)"
                  : "rgba(220, 53, 69, 0.25)";
                stroke = "#dc3545";
                opacity = 1;
              } else {
                fill = "rgba(82, 121, 111, 0.4)";
                stroke = "#52796f";
                opacity = isHovered ? 1 : 0;
              }

              return (
                apt.coords && (
                  <g key={`highlight-group-${apt.id}`}>
                    <polygon
                      points={scaleCoordinates(apt.coords)}
                      fill={fill}
                      stroke={stroke}
                      strokeWidth="2"
                      opacity={opacity}
                      style={{
                        transition: "opacity 0.3s ease, fill 0.3s ease",
                      }}
                    />
                    {isHovered && (
                      <g>
                        <rect
                          x={centroid.x - 40}
                          y={centroid.y - 25}
                          width="80"
                          height={isP ? 30 : 50}
                          fill="rgba(0, 0, 0, 0.1)"
                          stroke={isNotFree ? "#dc3545" : "#52796f"}
                          strokeWidth="0"
                          rx="4"
                          opacity="1"
                        />
                        <text
                          x={centroid.x}
                          y={centroid.y}
                          textAnchor="middle"
                          fill="white"
                          fontSize="14"
                          fontWeight="bold"
                          style={{ userSelect: "none" }}
                        >
                          {apt.id}
                        </text>
                        {!isP && (
                          <text
                            x={centroid.x}
                            y={centroid.y + 15}
                            textAnchor="middle"
                            fill="white"
                            fontSize="14"
                            fontWeight="bold"
                            style={{ userSelect: "none" }}
                          >
                            {apt.area}
                          </text>
                        )}
                      </g>
                    )}
                  </g>
                )
              );
            })}
          </svg>

          <map name="image-map">
            {filteredApartments.map((apt) => (
              <area
                key={apt.id}
                alt={t("floorMap.areaAlt", {
                  entrance: apt.entrance,
                  floor: apt.floor,
                  id: apt.id,
                })}
                coords={scaleCoordinates(apt.coords)}
                shape="poly"
                style={{ cursor: "pointer" }}
                onClick={(e) =>
                  handleAreaClick(e, apt.id, apt.floor, apt.entrance)
                }
                onMouseEnter={() => handleAreaHover(apt.id)}
                onMouseLeave={handleAreaLeave}
              />
            ))}
          </map>
        </div>

        <div className="overview-info">
          <div className="floor-header">
            {t("floorMap.floorLabel")}
            <select
              value={floor || ""}
              onChange={(e) => handleFloorChange(Number(e.target.value))}
            >
              {floors.map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </select>
          </div>

          {filteredApartments.length > 0 ? (
            <div className="apartment-list">
              {filteredApartments.map((apt) => {
                const firstChar = apt.id?.trim()?.charAt(0);
                const firstCharCode = firstChar?.charCodeAt(0);
                const isP = firstCharCode === 1055 || firstCharCode === 1087;

                const handleApartmentClick = (e) => {
                  if (isP) {
                    e.preventDefault();
                    return;
                  }
                };

                return (
                  <a
                    key={apt.id}
                    className={`apartment-item ${
                      hoveredApartment === apt.id ? "highlighted" : ""
                    } ${
                      apt.status.toLowerCase() === "резервиран"
                        ? "reserved"
                        : apt.status.toLowerCase() === "продаден"
                        ? "sold"
                        : "free"
                    }`}
                    onClick={handleApartmentClick}
                    onMouseEnter={() => handleAreaHover(apt.id)}
                    onMouseLeave={handleAreaLeave}
                  >
                    {t("floorMap.apartmentItem", {
                      id: apt.id,
                      area: apt.area,
                      status: getTranslatedStatus(apt.status),
                    })}
                  </a>
                );
              })}
            </div>
          ) : (
            <div style={{ padding: "20px", color: "#666" }}>
              {t("floorMap.noApartments")}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default Floor;
